import { DeleteCalendarEventInput, ExternalCalendarEvent } from '@griply/types';
import { removeExternalEventFromWindow } from '@griply/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteExternalCalendarEvent } from 'shared/services/backend-api';

import { queryKey as eventQueryKey } from './use-external-calendar-event-query';
import { queryKey as eventsQueryKey } from './use-external-calendar-events-query';

type CachedEvents = ExternalCalendarEvent[] | undefined;

export const useDeleteExternalCalendarEventMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (input: DeleteCalendarEventInput) =>
      deleteExternalCalendarEvent(input),

    // No snapshot listener behind the callable, so without this the deleted event keeps
    // rendering until the round-trip and refetch land.
    onMutate: async ({ eventId }) => {
      const previous = client.getQueriesData<ExternalCalendarEvent[]>({
        queryKey: [eventsQueryKey],
      });

      client.setQueriesData(
        { queryKey: [eventsQueryKey] },
        (events: CachedEvents) =>
          removeExternalEventFromWindow(events, eventId),
      );

      return { previous };
    },

    onError: (_error, _input, context) => {
      context?.previous.forEach(([key, events]) =>
        client.setQueryData<CachedEvents>(key, events),
      );
    },

    onSettled: async () => {
      await client.invalidateQueries({ queryKey: [eventsQueryKey] });
      await client.invalidateQueries({ queryKey: [eventQueryKey] });
    },
  });
};
